import {
  ChangeEvent,
  FunctionComponent,
  KeyboardEvent,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import { trackFeature } from '@/utils/adobeLaunch';
import { Delete } from '@/components/common/icons/Delete';
import { Minus } from '@/components/common/icons/Minus';
import { Plus } from '@/components/common/icons/Plus';
import './QuantitySelector.scss';

type Props = {
  children: ReactNode;
  inputId: string;
  className: string;
  labelClassName?: string;
  quantity: number;
  onChange: (quantity: number) => void;
  onDelete?: () => void;
  disabled?: boolean;
  minValue?: number;
  ariaLabel?: string;
  decreaseLabel?: string;
  increaseLabel?: string;
  trackingIncreaseFn?: () => void;
  trackingDecreaseFn?: () => void;
};

export const QuantitySelector: FunctionComponent<Props> = ({
  inputId,
  className,
  labelClassName,
  quantity,
  onChange,
  onDelete,
  disabled,
  minValue = 0,
  ariaLabel,
  decreaseLabel,
  increaseLabel,
  trackingIncreaseFn = () => {},
  trackingDecreaseFn = () => {},
  children,
}) => {
  const [value, setValue] = useState(quantity);
  useEffect(() => {
    setValue(quantity);
  }, [quantity]);

  function update(newQuantity: number) {
    if (newQuantity < minValue) {
      newQuantity = minValue;
    }

    setValue(newQuantity);

    if (onDelete && newQuantity === minValue) {
      onDelete();
    } else if (newQuantity !== quantity) {
      onChange(newQuantity);
    }
  }

  function handleIncrease() {
    trackingIncreaseFn();
    update(value + 1);
  }

  function handleDecrease() {
    trackingDecreaseFn();
    update(value - 1);
  }

  function handleInputChange(e: ChangeEvent<HTMLInputElement>) {
    trackFeature('size-selection-enter-quantity');
    setValue(parseInt(e.target.value, 10) || minValue);
  }

  function handleInputBlur() {
    update(value);
  }

  function handleInputKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter') return;
    update(value);
  }

  return (
    <div className={className}>
      <label className={labelClassName} htmlFor={inputId} aria-label={ariaLabel}>
        {children}
      </label>
      <span className="sprd-quantity">
        <input
          id={inputId}
          type="text"
          pattern="[0-9]*"
          inputMode="numeric"
          spellCheck="false"
          className="sprd-quantity__input"
          value={value}
          disabled={disabled}
          onChange={handleInputChange}
          onInput={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
        />
        <button
          type="button"
          title={decreaseLabel}
          className="sprd-quantity__btn sprd-quantity__btn--decrease"
          onClick={handleDecrease}
          disabled={disabled || value <= minValue}
        >
          {value <= minValue + 1 && onDelete ? (
            <Delete viewBox="-5 -5 42 42" className="sprd-quantity__icon" />
          ) : (
            <Minus className="sprd-quantity__icon" />
          )}
        </button>
        <button
          type="button"
          title={increaseLabel}
          className="sprd-quantity__btn sprd-quantity__btn--increase"
          onClick={handleIncrease}
          disabled={disabled}
        >
          <Plus className="sprd-quantity__icon" />
        </button>
      </span>
    </div>
  );
};
